import { useFormikContext } from "formik";
import { useLocalForms } from "../../../hooks/useForms";
import { usePois } from "../../../hooks/usePOIs";
import trans from "../../../utils";
import { Input, InputDate, TextAreaInput } from "../../Helpers/Input/Input";
import { InputSelect } from "../../Helpers/Input/RefactoredInput";
import dayjs from "dayjs";
import CustomAttributes from "../../Helpers/Input/CustomAttributes";
import { Tag, Switch, Tooltip } from "antd";
import { InfoCircleOutlined, UserOutlined } from "@ant-design/icons";
import { useTechnicians } from "../../../hooks/useTechnicians";
import { useEffect } from "react";

const taskTypeOptions = [
  { label: trans("inspection"), value: "inspection" },
  { label: trans("delivery"), value: "delivery" },
  { label: trans("survey"), value: "survey" },
  { label: trans("maintenance"), value: "maintenance" },
  { label: trans("other"), value: "other" },
];

const priorityOptions = [
  { label: trans("low"), value: "low" },
  { label: trans("medium"), value: "medium" },
  { label: trans("high"), value: "high" },
];

/* const flowOptions = [
  {
    label: trans("Sequential Task"),
    value: "sequential",
    description: trans(
      "Must finish all forms step by step to complete the task, end only when all are done"
    ),
  },
  {
    label: trans("Flexible Task"),
    value: "flexible",
    description:
      "Complete forms freely and end anytime, task can end with pending forms",
  },
]; */

const TasksFormBody = ({ isEditing, editData }) => {
  const {
    options: usersOptions,
    isFetching: techLoading,
    refetch: refetchTech,
  } = useTechnicians();
  const {
    options: formOptions,
    isFetching: formLoading,
    refetch: refetchForms,
  } = useLocalForms();
  const {
    options: poisOptions,
    isFetching: poiLoading,
    pois: allPois,
    refetch: refetchPois,
  } = usePois();

  // Add error handling for Formik context

  useEffect(() => {
    if (isEditing) {
      refetchPois();
      refetchForms();
      refetchTech();
    }
  }, [isEditing]);

  const formikContext = useFormikContext();

  if (!formikContext) {
    console.error("TasksFormBody must be used within a Formik component");
    return (
      <div className="alert alert-danger">
        {trans("Form context error. Please refresh the page.")}
      </div>
    );
  }

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
  } = formikContext;

  const selectedPoi = allPois?.find((p) => p.id === values.poiId);
  const isPoiGeofenced = selectedPoi?.geofenced === true;

  const getDisabled = (fieldName) => {
    if (!isEditing) return false;

    const status = editData?.data?.status;
    console.log("status", status);

    if (status === "done") {
      return true;
    }

    if (status === "inProgress") {
      const restricted = [
        "assignedToId",
        "poiId",
        "startTime",
        "type",
        "forms",
        "expectedDuration",
        "enableLocationTracking", // Add to restricted fields during progress
      ];
      return restricted.includes(fieldName);
    }

    return false;
  };

  // Check if location tracking should be available
  const isLocationTrackingAvailable = () => {
    return values.assignedToId && values.poiId;
  };

  return (
    <div className="form-section">
      <div className="row">
        <div className="col-lg-6">
          <Input
            title={trans("Task Name")}
            type="text"
            placeholder={trans("Ex. Task-001")}
            formstyle="form-input"
            id="name"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.name}
            touched={touched.name}
            max={128}
            disabled={getDisabled("name")}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="type"
            name="type"
            label={trans("Type")}
            placeholder={trans("Select Task Type")}
            options={taskTypeOptions}
            value={values.type}
            onChange={(val) => setFieldValue("type", val)}
            onBlur={() => setFieldTouched("type", true)}
            errors={errors.type}
            touched={touched.type}
            formstyle="form-input select-z-index"
            disabled={getDisabled("type")}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="assignedToId"
            name="assignedToId"
            label={trans("Assign to")}
            placeholder={trans("Select field staff")}
            options={usersOptions}
            loading={techLoading}
            value={values?.assignedToId || undefined}
            onChange={(val) => {
              setFieldValue("assignedToId", val || "");
              // Disable location tracking if no user is assigned
              if (!val && values.enableLocationTracking) {
                setFieldValue("enableLocationTracking", false);
              }
            }}
            onBlur={() => setFieldTouched("assignedToId", true)}
            errors={errors.assignedToId}
            touched={touched.assignedToId}
            formstyle="form-input select-z-index"
            disabled={getDisabled("assignedToId")}
            optionRender={(option) => (
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {option.data.avatar ? (
                  <img
                    src={option.data.avatar}
                    alt={option.label}
                    style={{ width: 24, height: 24, borderRadius: "50%" }}
                  />
                ) : (
                  <UserOutlined
                    style={{ width: 24, height: 24, borderRadius: "50%" }}
                  />
                )}
                <span>{option.label}</span>
              </div>
            )}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="priority"
            name="priority"
            label={trans("Priority")}
            placeholder={trans("Select Priority")}
            options={priorityOptions}
            value={values.priority}
            onChange={(val) => setFieldValue("priority", val)}
            onBlur={() => setFieldTouched("priority", true)}
            errors={errors.priority}
            touched={touched.priority}
            formstyle="form-input select-z-index"
            disabled={getDisabled("priority")}
          />
        </div>
        <div className="col-lg-6">
          <InputDate
            title={trans("Start Time")}
            id="startTime"
            name="startTime"
            showTime={{ format: "HH:mm" }}
            onBlur={(value) => setFieldTouched("startTime", true)}
            onChange={(value) =>
              setFieldValue(
                "startTime",
                value ? dayjs(value).toISOString() : ""
              )
            }
            errors={errors.startTime}
            touched={touched.startTime}
            value={values.startTime || ""}
            min={dayjs()}
            minTime={dayjs()}
            disabled={getDisabled("startTime")}
          />
        </div>
        <div className="col-lg-6">
          <Input
            title={trans("Duration (minutes)")}
            type="number"
            placeholder={trans("e.g. 90")}
            formstyle="form-input"
            id="expectedDuration"
            name="expectedDuration"
            value={values.expectedDuration}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10);
              setFieldValue("expectedDuration", isNaN(value) ? "" : value);
            }}
            onBlur={handleBlur}
            errors={errors.expectedDuration}
            touched={touched.expectedDuration}
            disabled={getDisabled("expectedDuration")}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="poiId"
            name="poiId"
            label={trans("POI")}
            placeholder={trans("Select POI")}
            options={poisOptions}
            loading={poiLoading}
            value={values.poiId}
            onChange={(val) => {
              setFieldValue("poiId", Number(val));
              // Disable location tracking if no POI is selected
              if (!val && values.enableLocationTracking) {
                setFieldValue("enableLocationTracking", false);
              }
            }}
            onBlur={() => setFieldTouched("poiId", true)}
            errors={errors.poiId}
            touched={touched.poiId}
            formstyle="form-input select-z-index"
            disabled={getDisabled("poiId")}
          />
        </div>
        <div className="col-lg-6">
          <InputSelect
            id="forms"
            name="forms"
            label={trans("Assign Form")}
            placeholder={trans("Select Forms")}
            searchLabelBoolean={true}
            options={formOptions.map((opt) => {
              if (isEditing) {
                const assignedForm = values.formsDetails?.find(
                  (f) => f.formId === opt.value
                );

                return {
                  ...opt,
                  label: (
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {opt.label}
                      {assignedForm && (
                        <Tag
                          color="green"
                          style={{ marginLeft: "8px", fontWeight: "bold" }}
                        >
                          {assignedForm.order}
                        </Tag>
                      )}
                    </span>
                  ),
                };
              } else {
                const index = values.forms.indexOf(opt.value);
                return {
                  ...opt,
                  label: (
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {opt.label}
                      {index !== -1 && (
                        <Tag
                          color="green"
                          style={{ marginLeft: "8px", fontWeight: "bold" }}
                        >
                          {index + 1}
                        </Tag>
                      )}
                    </span>
                  ),
                };
              }
            })}
            loading={formLoading}
            mode="multiple"
            value={values.forms}
            onChange={(val) => setFieldValue("forms", val)}
            onBlur={() => setFieldTouched("forms", true)}
            errors={errors.forms}
            touched={touched.forms}
            formstyle="form-input select-z-index"
            disabled={getDisabled("forms")}
            infoTooltip={trans(
              "The forms flow follows the same arrangement of choosing forms. Make sure you are entering them in the correct order."
            )}
          />
        </div>

        {/* Location Tracking Toggle */}
        {isPoiGeofenced && (
          <div className="col-lg-12">
            <div className="form-group">
              <label className="form-label d-flex align-items-center gap-2">
                {trans("Restrict Location")}
                <Tooltip
                  title={trans(
                    "Task will only started in its location. Requires both a POI location and assigned user to function properly."
                  )}
                >
                  <InfoCircleOutlined
                    style={{ color: "#1890ff", cursor: "help" }}
                  />
                </Tooltip>
              </label>
              <div className="d-flex align-items-center gap-3">
                <Switch
                  checked={values.enableLocationTracking}
                  onChange={(checked) => {
                    setFieldValue("enableLocationTracking", checked);
                    setFieldTouched("enableLocationTracking", true);
                  }}
                  disabled={
                    getDisabled("enableLocationTracking") ||
                    !isLocationTrackingAvailable()
                  }
                  checkedChildren={trans("ON")}
                  unCheckedChildren={trans("OFF")}
                />
                <span
                  className={`form-switch-label ${
                    values.enableLocationTracking
                      ? "text-success"
                      : "text-muted"
                  }`}
                >
                  {values.enableLocationTracking
                    ? trans("Location tracking enabled")
                    : trans("Location tracking disabled")}
                </span>
              </div>
              {!isLocationTrackingAvailable() && (
                <small className="form-text text-warning mt-1">
                  <InfoCircleOutlined className="me-1" />
                  {trans(
                    "Please assign a user and select a POI to enable location tracking"
                  )}
                </small>
              )}
              {values.enableLocationTracking &&
                isLocationTrackingAvailable() && (
                  <small className="form-text text-info mt-1">
                    {trans(
                      "The assigned user will be required to check in at the specified location"
                    )}
                  </small>
                )}
              {errors.enableLocationTracking &&
                touched.enableLocationTracking && (
                  <small className="form-text text-danger mt-1">
                    {errors.enableLocationTracking}
                  </small>
                )}
            </div>
          </div>
        )}
        <div className="col-lg-12">
          <TextAreaInput
            title={trans("Notes")}
            type="textarea"
            placeholder={trans("Add additional task details...")}
            formstyle="form-input"
            id="notes"
            name="notes"
            value={values.notes}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors.notes}
            touched={touched.notes}
            rows={3}
            disabled={getDisabled("notes")}
          />
        </div>
        {/* Select Flow - hidden; backend defaults to flexible
        <div className="col-lg-12 mt-3">
          <p className="fs-16 fw-500 brand-700 text-capitalize me-2">
            {trans("Select Flow")}
          </p>

          <Radio.Group
            onChange={(e) => setFieldValue("flowType", e.target.value)}
            value={values.flowType}
            className="d-flex flex-column gap-2"
          >
            {flowOptions.map((option) => (
              <div
                key={option.value}
                className="d-flex align-items-start p-3 rounded-3"
                style={{
                  cursor: "pointer",
                }}
                onClick={() => setFieldValue("flowType", option.value)}
              >
                <Radio
                  value={option.value}
                  style={{
                    transform: "scale(1.1)",
                    accentColor: "#52c41a",
                    marginRight: "12px",
                  }}
                />
                <div>
                  <div
                    className="fw-semibold"
                    style={{ color: "#000", fontSize: "15px" }}
                  >
                    {trans(option.label)}
                  </div>
                  <div
                    className="text-muted d-flex align-items-center mt-1"
                    style={{ fontSize: "13px" }}
                  >
                    <InfoCircleOutlined
                      style={{ color: "#8c8c8c", marginRight: "6px" }}
                    />
                    {trans(option.description)}
                  </div>
                </div>
              </div>
            ))}
          </Radio.Group>

          {errors.flowType && touched.flowType && (
            <small className="form-text text-danger mt-1">
              {errors.flowType}
            </small>
          )}
        </div>
        */}

        <div className="col-lg-12">
          <CustomAttributes setFieldValue={setFieldValue} />
        </div>
      </div>
    </div>
  );
};

export default TasksFormBody;
